import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LaunchIcon from "@mui/icons-material/Launch";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  cancelSubscription,
  changeSubscriptionPlan,
  getBillingPlans,
  getMyInvoices,
  getMySubscription,
  openBillingPortal,
  resumeSubscription,
  startSubscription,
} from "../api/apiClient";

/**
 * Billing.
 *
 * Stripe's hosted portal is the system of record for payment methods,
 * invoices and cancellation — this page shows the current state clearly and
 * hands off to the portal. Plan changes go through our API so the agreed
 * policy holds: upgrades apply immediately with proration, downgrades at the
 * next billing cycle.
 */

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

const formatMoney = (amount, currency) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  }).format(amount ?? 0);

const STATUS_LABEL = {
  active: { label: "Active", color: "success" },
  trialing: { label: "Trial", color: "info" },
  past_due: { label: "Payment overdue", color: "warning" },
  unpaid: { label: "Unpaid", color: "error" },
  canceled: { label: "Cancelled", color: "default" },
  incomplete: { label: "Incomplete", color: "warning" },
  paused: { label: "Paused", color: "default" },
};

export default function BillingPage() {
  const [subscription, setSubscription] = useState(null);
  const [account, setAccount] = useState({ build_credits: 0, free_blueprint_attempts: 0 });
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [subData, planData] = await Promise.all([getMySubscription(), getBillingPlans()]);
      setSubscription(subData.subscription);
      setAccount({
        build_credits: subData.build_credits,
        free_blueprint_attempts: subData.free_blueprint_attempts,
        has_billing_account: subData.has_billing_account,
      });
      setPlans(planData.plans || []);

      // Invoices come from Stripe and may be empty or unavailable; a failure
      // here should not blank the whole page.
      try {
        const invoiceData = await getMyInvoices();
        setInvoices(invoiceData.invoices || []);
      } catch {
        setInvoices([]);
      }
    } catch (err) {
      console.error("Failed to load billing", err);
      setError(err.response?.data?.detail || "Could not load your billing details.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscription") === "success") {
      setNotice("Thanks — your subscription is active.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [load]);

  const goToPortal = async (flow = null) => {
    setBusy(true);
    setError(null);
    try {
      const url = await openBillingPortal(flow);
      window.location.href = url;
    } catch (err) {
      console.error("Portal failed", err);
      setError(err.response?.data?.detail || "Could not open the billing portal.");
      setBusy(false);
    }
  };

  const handleSubscribe = async (planKey) => {
    setBusy(true);
    setError(null);
    try {
      const url = await startSubscription(planKey);
      window.location.href = url;
    } catch (err) {
      console.error("Checkout failed", err);
      setError(err.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  const handleChangePlan = async () => {
    if (!pendingPlan) return;
    setBusy(true);
    setError(null);
    try {
      const result = await changeSubscriptionPlan(pendingPlan.key);
      setNotice(result.message);
      setPendingPlan(null);
      await load();
    } catch (err) {
      console.error("Plan change failed", err);
      setError(err.response?.data?.detail || "Could not change your plan.");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await cancelSubscription();
      setNotice(result.message);
      setConfirmCancel(false);
      await load();
    } catch (err) {
      console.error("Cancel failed", err);
      setError(err.response?.data?.detail || "Could not cancel your subscription.");
    } finally {
      setBusy(false);
    }
  };

  const handleResume = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await resumeSubscription();
      setNotice(result.message);
      await load();
    } catch (err) {
      console.error("Resume failed", err);
      setError(err.response?.data?.detail || "Could not resume your subscription.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentPlanKey = subscription?.plan_key;
  const statusChip = STATUS_LABEL[subscription?.status] || { label: subscription?.status, color: "default" };

  /** Upgrade vs downgrade decides what we promise the user will happen. */
  const planRank = (key) => plans.find((p) => p.key === key)?.rank ?? 0;
  const isUpgradeTo = (plan) => !currentPlanKey || plan.rank > planRank(currentPlanKey);

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", px: { xs: 0.5, sm: 1 }, py: 2 }}>
      <Typography variant="h4" fontWeight={800}>
        Billing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Your plan, payment method and invoices.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {/* --- Current subscription --- */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={700}>
              Current subscription
            </Typography>
            {subscription ? (
              <>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5 }}>
                  <Typography variant="h5" fontWeight={800}>
                    {subscription.plan_name}
                  </Typography>
                  <Chip label={statusChip.label} size="small" color={statusChip.color} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subscription.cancel_at_period_end
                    ? `Ends ${formatDate(subscription.current_period_end)} — you keep full access until then.`
                    : `Renews ${formatDate(subscription.current_period_end)}.`}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
                  No subscription
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You are on pay-as-you-go credits.
                </Typography>
              </>
            )}
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<CreditCardIcon />}
              endIcon={<LaunchIcon fontSize="small" />}
              onClick={() => goToPortal()}
              disabled={busy}
            >
              Manage billing
            </Button>
            {subscription?.cancel_at_period_end ? (
              <Button variant="outlined" onClick={handleResume} disabled={busy}>
                Keep my subscription
              </Button>
            ) : (
              subscription?.is_active && (
                <Button color="error" variant="outlined" onClick={() => setConfirmCancel(true)} disabled={busy}>
                  Cancel subscription
                </Button>
              )
            )}
          </Stack>
        </Stack>

        {subscription?.scheduled_plan_name && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You are moving to <strong>{subscription.scheduled_plan_name}</strong> on{" "}
            {formatDate(subscription.scheduled_change_at)}. Until then you keep everything on your
            current plan.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
              Build credits
            </Typography>
            <Typography variant="h6" fontWeight={700}>{account.build_credits}</Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
              Free blueprints left
            </Typography>
            <Typography variant="h6" fontWeight={700}>{account.free_blueprint_attempts}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button size="small" onClick={() => goToPortal("payment_method")} disabled={busy}>
              Update payment method
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* --- Plans --- */}
      {plans.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" fontWeight={700}>Plans</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upgrades start straight away and are prorated. Downgrades take effect at your next
            billing date, so you keep what you have already paid for.
          </Typography>
          <Grid container spacing={2}>
            {plans.map((plan) => {
              const isCurrent = plan.key === currentPlanKey;
              return (
                <Grid item xs={12} md={4} key={plan.key}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      height: "100%",
                      borderRadius: 2,
                      borderColor: isCurrent ? "primary.main" : undefined,
                      borderWidth: isCurrent ? 2 : 1,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" fontWeight={800}>{plan.name}</Typography>
                      {isCurrent && <Chip label="Current" size="small" color="primary" />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ my: 1, minHeight: 40 }}>
                      {plan.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {plan.monthly_credits} credits / month
                    </Typography>
                    <Box sx={{ mt: 1.5 }}>
                      {isCurrent ? (
                        <Button fullWidth disabled variant="outlined">Your plan</Button>
                      ) : subscription?.is_active ? (
                        <Button
                          fullWidth
                          variant={isUpgradeTo(plan) ? "contained" : "outlined"}
                          onClick={() => setPendingPlan(plan)}
                          disabled={busy}
                        >
                          {isUpgradeTo(plan) ? "Upgrade" : "Downgrade"}
                        </Button>
                      ) : (
                        <Button fullWidth variant="contained" onClick={() => handleSubscribe(plan.key)} disabled={busy}>
                          Choose {plan.name}
                        </Button>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* --- Invoices --- */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Billing history</Typography>
            <Typography variant="body2" color="text.secondary">
              Every invoice on your account.
            </Typography>
          </Box>
          <Button size="small" endIcon={<LaunchIcon fontSize="small" />} onClick={() => goToPortal()} disabled={busy}>
            View in portal
          </Button>
        </Box>
        <Divider />
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 620 }} aria-label="billing history">
            <TableHead>
              <TableRow>
                <TableCell><strong>Invoice</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Download</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <ReceiptLongIcon color="disabled" />
                    <Typography variant="body2" color="text.secondary">
                      No invoices yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell>{invoice.number || invoice.id}</TableCell>
                    <TableCell>{formatDate(invoice.created)}</TableCell>
                    <TableCell>{formatMoney(invoice.amount_paid || invoice.amount_due, invoice.currency)}</TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status}
                        size="small"
                        color={invoice.status === "paid" ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {invoice.invoice_pdf && (
                        <MuiLink href={invoice.invoice_pdf} target="_blank" rel="noopener" underline="hover">
                          <PictureAsPdfIcon fontSize="small" sx={{ verticalAlign: "middle" }} /> PDF
                        </MuiLink>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={Boolean(pendingPlan)} onClose={() => setPendingPlan(null)}>
        <DialogTitle>
          {pendingPlan && isUpgradeTo(pendingPlan) ? "Upgrade" : "Downgrade"} to {pendingPlan?.name}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingPlan && isUpgradeTo(pendingPlan)
              ? "This starts immediately. You will be charged the prorated difference for the rest of this billing period."
              : `You keep your current plan until ${formatDate(subscription?.current_period_end)}, then move to ${pendingPlan?.name}. Nothing is charged now and there is no refund for the current period.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingPlan(null)}>Cancel</Button>
          <Button onClick={handleChangePlan} variant="contained" disabled={busy}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)}>
        <DialogTitle>Cancel your subscription?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You keep full access until {formatDate(subscription?.current_period_end)}, the end of
            the period you have already paid for. It will not renew after that, and you can undo
            this any time before then.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancel(false)}>Keep subscription</Button>
          <Button onClick={handleCancel} color="error" variant="contained" disabled={busy}>
            Cancel subscription
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
